import { useState, useEffect } from 'react';
import {
  AccountPaymentMethodsApiElementContentModel,
  AccountPaymentMethodsApiPropertiesModel,
  PaymentMethodDto,
} from '@/services/platform/api/model';
import { cn, ModuleProperties, useLocaleStore } from '@/lib/core';
import { useUIStore } from '@/lib/core/stores';
import ModuleWrapper from '@/components/shared/module/wrapper';
import {
  deletePaymentMethod,
  getPaymentMethods,
  setPreferredPaymentMethod,
  updatePaymentMethod,
} from '@/services/platform/data/payment-methods';
import { ProfilePaymentList } from '@/components/shared/profile/payment/list';

export const AccountPaymentMethodsApi = (props: ModuleProperties) => {
  const content = props.content as AccountPaymentMethodsApiElementContentModel | undefined;
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodDto[] | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const locale = useLocaleStore(state => state.locale);
  const setHideNavbar = useUIStore(state => state.setHideNavbar);

  // The screen runs full-bleed to the bottom edge, so the floating nav pill would otherwise sit
  // over the "add new payment method" CTA and intercept its taps.
  useEffect(() => {
    setHideNavbar(true);
    return () => setHideNavbar(false);
  }, [setHideNavbar]);

  // `getPaymentMethods` returns `undefined` on failure but `[]` when the user simply has no cards —
  // tracking the failure separately keeps a fetch error from being shown as "no payment methods".
  useEffect(() => {
    setLoading(true);
    getPaymentMethods(locale)
      .then(data => {
        setPaymentMethods(data ?? []);
        setLoadFailed(data === undefined);
      })
      .catch(() => {
        setPaymentMethods([]);
        setLoadFailed(true);
      })
      .finally(() => setLoading(false));
  }, [locale, refetchTrigger]);

  const properties = content?.properties as AccountPaymentMethodsApiPropertiesModel | undefined;

  if (!content) return null;

  return (
    <ModuleWrapper
      component={props}
      classNames={{
        base: cn(['module__account-payment-methods-api']),
        container: cn(['flex-1']),
        wrapper: cn(['py-0', 'flex-1']),
        grid: cn(['flex flex-col flex-1 gap-0']),
      }}
      isFullWidth={true}
      {...props}
    >
      <ProfilePaymentList
        paymentMethods={paymentMethods}
        loadFailed={loadFailed}
        properties={properties}
        loading={loading}
        onUpdate={async (id, expMonth, expYear, holderName, isPreferred) => {
          const success = await updatePaymentMethod(id, { expMonth, expYear, holderName }, locale);
          if (!success) throw new Error('Failed to update payment method');
          if (isPreferred) await setPreferredPaymentMethod(id);
          setRefetchTrigger(trigger => trigger + 1);
        }}
        onDelete={async id => {
          const success = await deletePaymentMethod(id);
          if (!success) throw new Error('Failed to delete payment method');
          setRefetchTrigger(trigger => trigger + 1);
        }}
      />
    </ModuleWrapper>
  );
};
